import { format } from 'date-fns';
import { TZDate } from '@date-fns/tz';
import { useCallback } from 'react';
import useSettingsStore from 'store/settingsStore';

export const convertDateToISO = (inputDate: string) => {
  return `${inputDate.substring(0, 4)}-${inputDate.substring(4, 6)}-${inputDate.substring(6, 8)}T${inputDate.substring(8, 10)}:${inputDate.substring(10, 12)}:${inputDate.substring(12, 14)}${inputDate.substring(14)}`;
};

const copenhagenTimeFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Copenhagen',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Returns the current wall-clock time in Europe/Copenhagen as 'HH:mm', for seeding
 * time pickers whose typed value is interpreted as Copenhagen local time.
 */
export const getCurrentCopenhagenTime = (): string => copenhagenTimeFormatter.format(new Date());

/**
 * Takes a Date whose UTC-labelled components represent a wall-clock date/time
 * selected by the user as Europe/Copenhagen local time, and returns the
 * corresponding instant in UTC as an ISO string.
 */
export const convertCopenhagenDateToUtcISOString = (date: Date): string => {
  const copenhagenDate = new TZDate(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
    'Europe/Copenhagen'
  );

  return new Date(copenhagenDate.getTime()).toISOString();
};

/**
 * Takes a Date representing a calendar date picked by the user as Europe/Copenhagen
 * local time, and returns the UTC instant for the start of that day (00:00:00) as an ISO string.
 */
export const convertCopenhagenStartOfDayToUtcISOString = (date: Date): string =>
  convertCopenhagenDateToUtcISOString(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0))
  );

/**
 * Takes a Date representing a calendar date picked by the user as Europe/Copenhagen
 * local time, and returns the UTC instant for the end of that day (23:59:59) as an ISO string.
 */
export const convertCopenhagenEndOfDayToUtcISOString = (date: Date): string =>
  convertCopenhagenDateToUtcISOString(
    new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59))
  );

const formatDateTimeForTimezone = (inputDate: string, timeZoneDisplay: 'utc' | 'cet') => {
  try {
    if (!inputDate || inputDate === '') {
      return '';
    }
    const regexDate = /^\d{14}Z$/;
    if (!regexDate.test(inputDate) && isNaN(Date.parse(inputDate))) {
      return '-';
    }

    const normalizedDate = regexDate.test(inputDate) ? convertDateToISO(inputDate) : inputDate;
    const convertedTzDate = new TZDate(normalizedDate, 'UTC');

    return `${format(convertedTzDate.withTimeZone(timeZoneDisplay === 'cet' ? 'Europe/Copenhagen' : 'UTC'), 'yyyy-MM-dd HH:mm:ss')}`; //  (${timeZoneDisplay.toUpperCase()})`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return '-';
  }
};

export const useFormatDateTimeForTimezone = () => {
  const timeZoneDisplay = useSettingsStore((state) => state.timezoneDisplay);

  return useCallback(
    (inputDate: string) => formatDateTimeForTimezone(inputDate, timeZoneDisplay),
    [timeZoneDisplay]
  );
};

const formatDateForTimezone = (
  inputDate: string | null | undefined,
  timeZoneDisplay: 'utc' | 'cet'
) => {
  try {
    if (!inputDate || inputDate === '') {
      return '-';
    }
    const regexDate = /^\d{14}Z$/;
    if (!regexDate.test(inputDate) && isNaN(Date.parse(inputDate))) {
      return '-';
    }

    const normalizedDate = regexDate.test(inputDate) ? convertDateToISO(inputDate) : inputDate;
    const convertedTzDate = new TZDate(normalizedDate, 'UTC');

    return `${format(convertedTzDate.withTimeZone(timeZoneDisplay === 'cet' ? 'Europe/Copenhagen' : 'UTC'), 'yyyy-MM-dd')}`;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return '-';
  }
};

export const useFormatDateForTimezone = () => {
  const timeZoneDisplay = useSettingsStore((state) => state.timezoneDisplay);

  return useCallback(
    (inputDate: string | null | undefined) => formatDateForTimezone(inputDate, timeZoneDisplay),
    [timeZoneDisplay]
  );
};
